// Én service på «Servicer på verkstedet» (/verkstedet/:id): bare informasjon til butikkansatte,
// pluss kommentaren til mekanikeren (produkt.kommentar), som er det eneste som kan endres her.
// WorkshopDetails er uendret. Servicen hentes fra lista (se verkstedet/felles.js).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Tilbake, Sidetopp, knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import KundeBanner from '../components/KundeBanner';
import { isLeder } from '../employeeAccess';
import { tilDelelinjer, beregnTotaler } from '../features/invoice/fakturaGrunnlag';
import {
  STATUS_FARGE, statusNavn, butikkFor, ferdigDato, leveringsDato, kundeNavn, produktTekst,
  dagerTil, narTekst, hentServicer, lagreKommentar,
} from '../verkstedet/felles';

const kr = (n) => `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(Number(n) || 0)} kr`;
const datoTid = (d) => {
  const x = d ? new Date(d) : null;
  return x && !isNaN(x)
    ? x.toLocaleString('nb-NO', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
};
const minutter = (m) => {
  const n = Math.max(0, Number(m) || 0);
  const h = Math.floor(n / 60);
  const r = n % 60;
  return h && r ? `${h} t ${r} min` : h ? `${h} t` : `${r} min`;
};

function Kort({ tittel, id, children, className = '' }) {
  return (
    <section aria-labelledby={id} className={`space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-3 ${className}`}>
      <h2 id={id} className="text-base font-bold text-slate-900">{tittel}</h2>
      {children}
    </section>
  );
}

export default function ServiceInfo() {
  const { id } = useParams();
  const { state } = useLocation();
  const [svc, setSvc] = useState(state?.service?._id === id ? state.service : null);
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState('');

  const [kommentar, setKommentar] = useState(svc?.produkt?.kommentar || '');
  const [lagret, setLagret] = useState(svc?.produkt?.kommentar || ''); // det serveren hadde da vi hentet
  const [lagrer, setLagrer] = useState(false);
  const [melding, setMelding] = useState('');
  const [konflikt, setKonflikt] = useState(null); // nyere kommentar fra serveren
  const feltRef = useRef(null);
  const [tilSlutten, setTilSlutten] = useState(0); // øker når markøren skal settes bakerst i feltet

  // Henter alltid på nytt (også når servicen kom med fra lista), så det som vises er ferskt.
  useEffect(() => {
    let aktiv = true;
    hentServicer()
      .then((liste) => {
        if (!aktiv) return;
        const funnet = liste.find((s) => s._id === id) || null;
        setSvc(funnet);
        if (funnet) {
          setKommentar(funnet.produkt?.kommentar || '');
          setLagret(funnet.produkt?.kommentar || '');
        }
      })
      .catch((e) => {
        console.error('Feil ved henting av servicen:', e);
        if (aktiv) setFeil('Klarte ikke å hente servicen. Last siden på nytt om litt.');
      })
      .finally(() => { if (aktiv) setLastet(true); });
    return () => { aktiv = false; };
  }, [id]);

  const totaler = useMemo(
    () => (svc ? beregnTotaler(svc.serviceLines || [], tilDelelinjer(svc.partsLines), svc.fraktLines || []) : null),
    [svc]
  );

  const leggTilSamtale = () => {
    const na = new Date();
    const stempel = `${na.toLocaleDateString('nb-NO', { day: 'numeric', month: 'numeric' })} kl. ${na.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`;
    const ansatt = Cookies.get('selectedEmployee') || '';
    const linje = `${stempel}${ansatt ? ` (${ansatt})` : ''}: Kunden har ringt – `;
    const ny = kommentar.trim() ? `${kommentar.replace(/\s+$/, '')}\n${linje}` : linje;
    setKommentar(ny);
    setMelding('');
    setTilSlutten((n) => n + 1);
  };

  // Etter «Kunden har ringt»: fokus i feltet med markøren bakerst, så den ansatte kan skrive videre.
  useEffect(() => {
    if (!tilSlutten) return;
    const el = feltRef.current;
    if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
  }, [tilSlutten]);

  const lagre = async (likevel = false) => {
    if (lagrer || !svc) return;
    setLagrer(true);
    setMelding('');
    try {
      // Har noen (f.eks. mekanikeren) endret kommentaren siden vi hentet den, skal den ikke
      // overskrives uten at den ansatte ser det.
      if (!likevel) {
        const fersk = (await hentServicer()).find((s) => s._id === svc._id);
        const naa = fersk?.produkt?.kommentar || '';
        if (naa !== lagret) {
          setKonflikt(naa);
          setLagrer(false);
          return;
        }
      }
      const oppdatert = await lagreKommentar(svc._id, kommentar.trim());
      setSvc(oppdatert);
      setKommentar(oppdatert?.produkt?.kommentar || '');
      setLagret(oppdatert?.produkt?.kommentar || '');
      setKonflikt(null);
      setMelding('Kommentaren er lagret. Mekanikeren ser den på servicen.');
    } catch (e) {
      console.error('Feil ved lagring av kommentar:', e);
      setMelding('');
      setFeil('Klarte ikke å lagre kommentaren. Prøv igjen.');
    } finally {
      setLagrer(false);
    }
  };

  if (!svc) {
    return (
      <div className="mx-auto max-w-7xl space-y-3">
        <Tilbake til="/verkstedet">Tilbake til servicelisten</Tilbake>
        {feil ? (
          <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>
        ) : !lastet ? (
          <p className="text-slate-700">Henter servicen …</p>
        ) : (
          <h1 className="text-xl font-bold text-slate-900">Fant ikke servicen</h1>
        )}
      </div>
    );
  }

  const status = String(svc.status || '').toLowerCase();
  const ferdig = ferdigDato(svc);
  const levert = leveringsDato(svc);
  const linjer = svc.serviceLines || [];
  const deler = tilDelelinjer(svc.partsLines);
  const logger = [...(svc.produkt?.statusLogs || [])].reverse();
  // Som WorkshopDetails: kommentaren fra siste «mottatt» i statusloggen.
  const mottakKommentar = svc.produkt?.mottakKommentar
    || logger.find((l) => String(l.status || '').toLowerCase() === 'mottatt')?.comment
    || '';
  const verkstedKommentarer = [
    mottakKommentar && { etikett: 'Kommentar ved mottak', tekst: mottakKommentar },
    svc.arbeidsbeskrivelse && { etikett: 'Arbeidsbeskrivelse', tekst: svc.arbeidsbeskrivelse },
    svc.produkt?.lastStatusComment && { etikett: 'Siste statuskommentar', tekst: svc.produkt.lastStatusComment },
  ].filter(Boolean);
  const endret = kommentar.trim() !== (lagret || '').trim();

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake til="/verkstedet">Tilbake til servicelisten</Tilbake>

      <Sidetopp
        ikon="skiftenokkel"
        farge="verksted"
        tittel={produktTekst(svc)}
        undertittel={[svc.serviceRef && `Ref. ${svc.serviceRef}`, butikkFor(svc)].filter(Boolean).join(' · ')}
      />

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {/* Status og tider */}
      <section aria-labelledby="si-status" className="space-y-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3 sm:p-3">
        <h2 id="si-status" className="flex flex-wrap items-center gap-2 text-base font-bold text-slate-900">
          Status
          <span className={`rounded-full px-3 py-1 text-base font-semibold ${STATUS_FARGE[status] || 'bg-slate-100 text-slate-800'}`}>{statusNavn(svc.status)}</span>
          {svc.produkt?.prioritering?.haster && <span className="rounded-full bg-red-100 px-3 py-1 text-base font-semibold text-red-900">Haster</span>}
        </h2>
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <div className="col-span-2 min-w-0 rounded-xl bg-white p-2.5 ring-1 ring-emerald-200 sm:col-span-1">
            <dt className="text-sm text-slate-600">Forventet ferdig</dt>
            <dd className={`text-lg font-bold first-letter:uppercase ${ferdig && dagerTil(ferdig) < 0 ? 'text-amber-800' : 'text-slate-900'}`}>
              {ferdig ? narTekst(ferdig) : 'Ikke satt'}
            </dd>
            {ferdig && dagerTil(ferdig) < 0 && <dd className="text-sm font-medium text-amber-800">Datoen er passert</dd>}
          </div>
          <div className="min-w-0 rounded-xl bg-white p-2.5 ring-1 ring-slate-200">
            <dt className="text-sm text-slate-600">Levert</dt>
            <dd className="font-semibold text-slate-900 first-letter:uppercase">{levert ? narTekst(levert) : narTekst(svc.createdAt ? new Date(svc.createdAt) : null) || 'Ukjent'}</dd>
          </div>
          <div className="min-w-0 rounded-xl bg-white p-2.5 ring-1 ring-slate-200">
            <dt className="text-sm text-slate-600">Registrert</dt>
            <dd className="font-semibold text-slate-900">{datoTid(svc.createdAt) || 'Ukjent'}</dd>
          </div>
        </dl>
      </section>

      {/* Kommentar til mekanikeren: det eneste som kan endres her */}
      <section aria-labelledby="si-kommentar" className="space-y-2 rounded-2xl border-2 border-indigo-200 bg-indigo-50/60 p-3 sm:p-3">
        <h2 id="si-kommentar" className="text-base font-bold text-slate-900">Kommentar til mekanikeren</h2>
        <p className="text-slate-700">
          Skriv hva kunden har sagt eller spurt om. Mekanikeren ser kommentaren på servicen.
        </p>
        <label htmlFor="si-kommentar-felt" className="sr-only">Kommentar til mekanikeren</label>
        <textarea
          id="si-kommentar-felt"
          ref={feltRef}
          value={kommentar}
          onChange={(e) => { setKommentar(e.target.value); setMelding(''); }}
          rows={5}
          maxLength={2000}
          className="w-full rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
        />
        {konflikt !== null && (
          <div role="alert" className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-amber-950">
            <p className="font-semibold">Kommentaren er endret av noen andre mens du skrev.</p>
            <p className="text-sm">Slik står den nå:</p>
            <p className="whitespace-pre-line rounded-lg bg-white p-2 text-sm text-slate-800 [overflow-wrap:anywhere]">{konflikt || '(tom)'}</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={knappSekundar}
                onClick={() => { setKommentar(`${konflikt}${konflikt ? '\n' : ''}${kommentar.startsWith(lagret) ? kommentar.slice(lagret.length).trim() : kommentar}`); setLagret(konflikt); setKonflikt(null); }}
              >
                Legg teksten min til den nye
              </button>
              <button type="button" className={knappSekundar} onClick={() => lagre(true)}>
                Lagre min versjon likevel
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={leggTilSamtale} className={knappSekundar}>
            <Ikon navn="melding" className="w-5 h-5" /> Kunden har ringt
          </button>
          <button type="button" onClick={() => lagre(false)} disabled={lagrer || !endret} className={knappPrimar}>
            <Ikon navn="hake" className="w-6 h-6" /> {lagrer ? 'Lagrer …' : 'Lagre kommentar'}
          </button>
          <p role="status" className="font-medium text-emerald-800">{melding}</p>
        </div>
      </section>

      {/* Kunden: felles banner med lenke til kundekortet (KundeBanner, vinnsel 30.10) */}
      <KundeBanner
        kundeId={svc.customerId}
        navn={kundeNavn(svc) || 'Ukjent'}
        telefon={svc.customerSnapshot?.phoneNumber || 'Ikke registrert'}
        epost={svc.customerSnapshot?.email}
      />

      {/* Hva som skal gjøres */}
      <Kort id="si-arbeid" tittel="Hva som skal gjøres">
        {linjer.length === 0 && deler.length === 0 ? (
          <p className="text-slate-700">Ingen arbeid eller deler er lagt inn ennå.</p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {linjer.map((l, i) => (
              <li key={`a${i}`} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5">
                <span className="min-w-0 flex-1 basis-48">
                  <span className="block font-semibold text-slate-900 [overflow-wrap:anywhere]">{l.navn || 'Arbeid'}</span>
                  {l.kommentar && <span className="block text-sm text-slate-700 [overflow-wrap:anywhere]">{l.kommentar}</span>}
                </span>
                <span className="text-sm text-slate-700">
                  {l.tidsbruk ? `${minutter(l.tidsbruk)} · ` : ''}
                  {l.reklamasjon || l.isWarranty ? 'Reklamasjon' : l.gratisarbeid ? 'Gratis' : kr(l.pris)}
                </span>
              </li>
            ))}
            {deler.map((d, i) => (
              <li key={`d${i}`} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5">
                <span className="min-w-0 flex-1 basis-48 text-slate-900 [overflow-wrap:anywhere]">
                  <span className="font-semibold">{[d.brand, d.product].filter(Boolean).join(' ') || 'Del'}</span>
                  {d.qty > 1 && <span className="text-slate-700"> × {d.qty}</span>}
                </span>
                <span className="text-sm text-slate-700">{d.isWarranty ? 'Reklamasjon' : kr(d.price * Math.max(1, d.qty))}</span>
              </li>
            ))}
          </ul>
        )}
        {totaler && (linjer.length > 0 || deler.length > 0) && (
          <p className="flex flex-wrap items-baseline justify-between gap-2 border-t border-slate-200 pt-3">
            <span className="font-semibold text-slate-900">Pris så langt</span>
            <span className="text-lg font-bold text-slate-900">{kr(totaler.total)}</span>
          </p>
        )}
        <p className="text-sm text-slate-600">Prisen kan endre seg hvis verkstedet finner mer som må gjøres.</p>
      </Kort>

      {verkstedKommentarer.length > 0 && (
        <Kort id="si-verksted" tittel="Fra verkstedet">
          <dl className="space-y-2">
            {verkstedKommentarer.map((k) => (
              <div key={k.etikett}>
                <dt className="text-sm text-slate-600">{k.etikett}</dt>
                <dd className="whitespace-pre-line text-slate-900 [overflow-wrap:anywhere]">{k.tekst}</dd>
              </div>
            ))}
          </dl>
        </Kort>
      )}

      {logger.length > 0 && (
        <Kort id="si-historikk" tittel="Statushistorikk">
          <ol className="space-y-2">
            {logger.map((l, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="w-36 shrink-0 text-sm text-slate-600">{datoTid(l.at)}</span>
                <span className="min-w-0 flex-1 basis-48">
                  <span className="font-semibold text-slate-900">{statusNavn(l.status)}</span>
                  {l.by?.name && <span className="text-slate-700"> · {l.by.name}</span>}
                  {l.comment && <span className="block text-sm text-slate-700 [overflow-wrap:anywhere]">{l.comment}</span>}
                </span>
              </li>
            ))}
          </ol>
        </Kort>
      )}

      {isLeder() && (
        <p className="text-sm text-slate-600">
          Leder: <Link to={`/workshop/${svc._id}`} className="font-semibold text-indigo-700 underline">åpne full visning i verkstedet</Link>.
        </p>
      )}
    </div>
  );
}
